import { useMutation } from '@apollo/client';
import {
    GRAPHQL_APROVAR_SAQUE,
    GRAPHQL_REPROCESSAR_SAQUE,
    GRAPHQL_REPROVAR_SAQUE,
    GRAPHQL_SINCRONIZAR_SAQUE,
} from 'gql/saque';
import { useCallback, useContext } from 'react';
import { useModalHistory } from 'routes/admin/hook/useModalHistory';
import { AuthContext } from 'routes/auth.context';
import { ISaque } from './index.d';

const moeda = (valor: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(valor || 0);

export const useAcoesSaque = (props) => {
    const { alertConfirm } = useContext(AuthContext);
    const history = useModalHistory();
    const [aprovar] = useMutation(GRAPHQL_APROVAR_SAQUE);
    const [reprovar] = useMutation(GRAPHQL_REPROVAR_SAQUE);
    const [reprocessar] = useMutation(GRAPHQL_REPROCESSAR_SAQUE);
    const [sincronizar] = useMutation(GRAPHQL_SINCRONIZAR_SAQUE);

    const executar = useCallback(
        async (acao: () => Promise<any>, campo: string) => {
            try {
                const { data } = await acao();
                const saque: ISaque = data?.[campo];
                props.onSubscriptionData(saque);
                props.onAtualizado?.();
                // A aprovação pode ser aceita e a transferência falhar no Asaas
                if (saque?.status === 'FALHOU') {
                    await alertConfirm({
                        tipo: 'erro',
                        titulo: 'Transferência não realizada',
                        conteudo:
                            saque.motivoFalha ||
                            'O Asaas não aceitou a transferência.',
                    });
                }
            } catch ({ message }: any) {
                await alertConfirm({
                    tipo: 'erro',
                    titulo: 'Erro',
                    conteudo: message as string,
                });
            }
        },
        [props.onSubscriptionData, props.onAtualizado]
    );

    const handleAprovar = useCallback(
        async (saque: ISaque) => {
            const confirmado = await alertConfirm({
                tipo: 'info',
                titulo: 'Aprovar saque',
                textOk: 'Aprovar e pagar',
                textCancelar: 'Voltar',
                conteudo: `Será enviado um PIX de ${moeda(
                    saque.valorLiquido
                )} para ${saque.entregadorNome} (${saque.tipoPix}: ${
                    saque.chavePix
                }).`,
            });
            if (!confirmado) return;
            await executar(
                () => aprovar({ variables: { id: saque.id } }),
                'aprovarSaque'
            );
        },
        [executar]
    );

    const handleReprovar = useCallback(
        async (saque: ISaque) => {
            const motivo = await alertConfirm({
                tipo: 'input',
                titulo: 'Reprovar saque',
                textOk: 'Reprovar',
                textCancelar: 'Voltar',
                conteudo: `Informe o motivo da reprovação do saque de ${saque.entregadorNome}. As corridas voltam para o saldo do entregador.`,
            });
            if (typeof motivo !== 'string' || !motivo.trim()) return;
            await executar(
                () => reprovar({ variables: { id: saque.id, motivo } }),
                'reprovarSaque'
            );
        },
        [executar]
    );

    const handleReprocessar = useCallback(
        async (saque: ISaque) => {
            const confirmado = await alertConfirm({
                tipo: 'info',
                titulo: 'Reenviar ao Asaas',
                textOk: 'Reenviar',
                textCancelar: 'Voltar',
                conteudo: saque.envioSemConfirmacao
                    ? `O último envio ficou sem resposta do Asaas. Confirme no painel do Asaas que a transferência de ${moeda(
                          saque.valorLiquido
                      )} NÃO foi criada antes de reenviar, para não pagar em dobro.`
                    : `Será enviado um novo PIX de ${moeda(
                          saque.valorLiquido
                      )} para ${saque.entregadorNome}.`,
            });
            if (!confirmado) return;
            await executar(
                () =>
                    reprocessar({
                        variables: {
                            id: saque.id,
                            forcar: !!saque.envioSemConfirmacao,
                        },
                    }),
                'reprocessarSaque'
            );
        },
        [executar]
    );

    const handleSincronizar = useCallback(
        async (saque: ISaque) => {
            await executar(
                () => sincronizar({ variables: { id: saque.id } }),
                'sincronizarSaque'
            );
        },
        [executar]
    );

    const handleComprovante = useCallback((saque: ISaque) => {
        window.open(saque.comprovanteUrl, '_blank', 'noopener');
    }, []);

    const handleDetalhes = useCallback(
        (saque: ISaque) => {
            history.push(['detalhes', saque.id]);
        },
        [history.push]
    );

    return {
        handleDetalhes,
        handleAprovar,
        handleReprovar,
        handleReprocessar,
        handleSincronizar,
        handleComprovante,
    };
};
